import React from 'react';
import { cn } from '../../utils/cn';

const FormGroup = ({ 
  label, 
  children, 
  error, 
  hint, 
  required = false,
  className = '',
  direction = 'vertical' // vertical or horizontal
}) => {
  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label className={cn(
          'block text-sm font-medium text-gray-700',
          direction === 'horizontal' && 'inline-block w-32'
        )}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className={direction === 'horizontal' ? 'inline-block flex-1 ml-2' : 'block'}>
        {children}
        
        {hint && !error && (
          <p className="mt-1 text-xs text-gray-500">{hint}</p>
        )}
        
        {error && (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        )}
      </div>
    </div>
  );
};

const Input = ({ 
  type = 'text',
  placeholder,
  value,
  onChange,
  disabled = false,
  error = false,
  className = '',
  ...props 
}) => {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={cn(
        'w-full px-3 py-2 border rounded-lg transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
        error 
          ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
          : 'border-gray-300',
        className
      )}
      {...props}
    />
  );
};

const Textarea = ({ 
  placeholder,
  value,
  onChange,
  disabled = false,
  error = false,
  rows = 3,
  className = '',
  ...props 
}) => {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      rows={rows}
      className={cn(
        'w-full px-3 py-2 border rounded-lg transition-colors resize-vertical',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
        error 
          ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
          : 'border-gray-300',
        className
      )}
      {...props}
    />
  );
};

const Select = ({ 
  value,
  onChange,
  disabled = false,
  error = false,
  children,
  placeholder,
  size = 'normal', // compact, normal
  className = '',
  ...props 
}) => {
  const sizeClasses = {
    compact: 'px-2 py-1 text-xs',
    normal: 'px-3 py-2'
  };
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={cn(
      'border rounded-lg transition-colors appearance-none bg-white',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
      'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
      sizeClasses[size],
      error 
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300',
      className
      )}
      {...props}
    >
      {placeholder && (
        <option value="" disabled>{placeholder}</option>
      )}
      {children}
    </select>
  );
};

const Checkbox = ({ 
  checked,
  onChange,
  disabled = false,
  label,
  error = false,
  className = '',
  ...props 
}) => {
  return (
    <label className={cn(
      'flex items-center gap-2 cursor-pointer',
      disabled && 'cursor-not-allowed opacity-50',
      className
    )}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={cn(
          'rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2',
          error && 'border-red-300 focus:ring-red-500',
          'disabled:cursor-not-allowed'
        )}
        {...props}
      />
      {label && (
        <span className="text-sm text-gray-700">{label}</span>
      )}
    </label>
  );
};

const Radio = ({ 
  name,
  value,
  checked,
  onChange,
  disabled = false,
  label,
  error = false,
  className = '',
  ...props 
}) => {
  return (
    <label className={cn(
      'flex items-center gap-2 cursor-pointer',
      disabled && 'cursor-not-allowed opacity-50',
      className
    )}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={cn(
          'border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2',
          error && 'border-red-300 focus:ring-red-500',
          'disabled:cursor-not-allowed'
        )}
        {...props}
      />
      {label && (
        <span className="text-sm text-gray-700">{label}</span>
      )}
    </label>
  );
};

const RadioGroup = ({ 
  name,
  value,
  onChange,
  options = [],
  disabled = false,
  error = false,
  direction = 'vertical', // vertical or horizontal
  className = ''
}) => {
  return (
    <div className={cn(
      direction === 'horizontal' ? 'flex gap-4' : 'space-y-2',
      className
    )}>
      {options.map((option) => (
        <Radio
          key={option.value}
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          label={option.label}
        />
      ))}
    </div>
  );
};

const TagInput = ({ 
  tags = [],
  onAddTag,
  onRemoveTag,
  placeholder = "Type and press Enter",
  disabled = false,
  error = false,
  tagColor = 'blue',
  className = ''
}) => {
  const [inputValue, setInputValue] = React.useState('');

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = inputValue.trim();
      if (value && !tags.includes(value)) {
        onAddTag(value);
        setInputValue('');
      }
    }
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    purple: 'bg-purple-100 text-purple-800',
    orange: 'bg-orange-100 text-orange-800',
    red: 'bg-red-100 text-red-800'
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Tags Display */}
      <div className={cn(
        'flex flex-wrap gap-1 min-h-[40px] p-2 border rounded-lg',
        error ? 'border-red-300' : 'border-gray-300'
      )}>
        {tags.map((tag, index) => (
          <span 
            key={index} 
            className={cn(
              'px-2 py-1 rounded text-sm flex items-center gap-1',
              colorClasses[tagColor] || colorClasses.blue
            )}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemoveTag(index)}
                className="hover:opacity-75 font-bold"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {tags.length === 0 && (
          <span className="text-gray-400 text-sm py-1">No tags added yet</span>
        )}
      </div>
      
      {/* Input */}
      {!disabled && (
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          error={error}
        />
      )}
    </div>
  );
};

const FieldGroup = ({ 
  title, 
  children, 
  collapsible = false,
  defaultOpen = true,
  variant = 'default', // default, highlight, warning, success
  className = ''
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const variantClasses = {
    default: 'bg-gray-50 border-gray-200',
    highlight: 'bg-blue-50 border-blue-200 text-blue-900',
    warning: 'bg-orange-50 border-orange-200 text-orange-900',
    success: 'bg-green-50 border-green-200 text-green-900'
  };

  return (
    <div className={cn('border rounded-lg', variantClasses[variant], className)}>
      {title && (
        <div className="px-4 py-3 border-b border-current border-opacity-20">
          {collapsible ? (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 font-medium w-full text-left"
            >
              <span className={`transform transition-transform ${isOpen ? 'rotate-90' : ''}`}>
                ▶
              </span>
              {title}
            </button>
          ) : (
            <h4 className="font-medium">{title}</h4>
          )}
        </div>
      )}
      
      {(!collapsible || isOpen) && (
        <div className="p-4 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Export all components
FormGroup.Input = Input;
FormGroup.Textarea = Textarea;
FormGroup.Select = Select;
FormGroup.Checkbox = Checkbox;
FormGroup.Radio = Radio;
FormGroup.RadioGroup = RadioGroup;
FormGroup.TagInput = TagInput;
FormGroup.FieldGroup = FieldGroup;

export default FormGroup;